// R1c — CRM contacts list. Server-scoped to tenant via /api/contacts.
// Read-only in R1c (edit flow lives inside LeadDetail / Opportunity today).
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import { api, formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { UserSquare2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";
import ComplianceProofButton from "../../design-system/compliance/ComplianceProofButton";
// Release C-1 — Open in Microsoft 365 Mail for the contact-linked mailbox.
import OpenInMicrosoftMailButton from "../../design-system/microsoft365/OpenInMicrosoftMailButton";

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [attachId, setAttachId] = useState(null);
  const [attachReload, setAttachReload] = useState(0);

  useEffect(() => {
    api.get("/contacts")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((c) =>
      `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase().includes(s)
      || (c.email || "").toLowerCase().includes(s)
      || (c.phone || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  return (
    <AppShell
      title="CRM · Contacts"
      subtitle={`${items.length} contact(s) · tenant-scoped`}
    >
      <div className="mb-4 flex items-center gap-2">
        <Input
          type="search"
          placeholder="Search by name, email or phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-80 h-9"
          data-testid="contacts-search"
          aria-label="Search contacts"
        />
        <span className="text-xs font-mono text-erf-dark/60" data-testid="contacts-count">
          {filtered.length} of {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <EmptyState
          icon={UserSquare2}
          title="No contacts yet"
          subtitle="Contacts are created automatically when you convert a Lead. Convert a lead in /crm/leads to see them here."
        />
      ) : (
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Phone</th>
                <th className="text-left px-3 py-2">Account</th>
                <th className="text-left px-3 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <React.Fragment key={c.id}>
                <tr className="border-t border-erf-borderCream/60 cursor-pointer"
                    data-testid={`contact-row-${c.id}`}
                    onClick={() => setAttachId(attachId === c.id ? null : c.id)}>
                  <td className="px-3 py-2 font-nav font-semibold text-erf-dark">
                    <Link to={`/crm/contacts/${c.id}`} onClick={(e) => e.stopPropagation()} className="underline inline-flex min-h-[44px] items-center" data-testid={`contact-open-${c.id}`}>
                      {c.first_name} {c.last_name}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    {c.email ? (
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-erf-dark hover:text-erf-goldHover underline decoration-erf-gold/60 underline-offset-2">
                        <Mail className="w-3 h-3" aria-hidden="true" /> {c.email}
                      </a>
                    ) : <span className="text-erf-dark/50">—</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    {c.phone ? (
                      <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-erf-dark hover:text-erf-goldHover">
                        <Phone className="w-3 h-3" aria-hidden="true" /> {c.phone}
                      </a>
                    ) : <span className="text-erf-dark/50">—</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-erf-dark/70">
                    {c.account_id ? String(c.account_id).slice(0, 8) : "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-erf-dark/70">
                    {(c.created_at || "").slice(0, 10) || "—"}
                  </td>
                </tr>
                {attachId === c.id && (
                  <tr key={`${c.id}-attach`} data-testid={`contact-attach-${c.id}`}>
                    <td colSpan={5} className="px-4 py-3 bg-erf-cream/40">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] font-mono uppercase text-erf-dark/60">
                          Attachments for {c.first_name} {c.last_name} (restricted)
                        </div>
                        <div className="flex items-center gap-2">
                          <ComplianceProofButton recordType="contact" recordId={c.id} />
                          <OpenInMicrosoftMailButton
                            surface="contact_detail"
                            recordType="contact"
                            recordId={c.id}
                          />
                          <InformationIntake
                            recordType="contact" recordId={c.id}
                            category="other" confidentiality="restricted"
                            capabilities={["upload", "multi", "paste", "existing", "request_info", "photo"]}
                            onChanged={() => setAttachReload((k) => k + 1)}
                          />
                        </div>
                      </div>
                      <AttachmentsPanel recordType="contact" recordId={c.id} reload={attachReload} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
